import releasesData from "@/content/releases.json";
import { useT } from "@/lib/i18n";
import { Reveal } from "./Reveal";
import { SectionHeading } from "./SectionHeading";

export interface Release {
  id: string;
  title: string;
  artist: string;
  format?: string;
  year?: string;
  links?: { youtube?: string; spotify?: string; appleMusic?: string };
}

const SECTION_CLS =
  "mx-auto max-w-7xl scroll-mt-24 px-6 py-24 md:px-12 md:py-32 xl:px-24";

export { SECTION_CLS };

export const Releases = () => {
  const t = useT();
  const items = releasesData.releases as Release[];
  const chips = [t.releases.chipYT, t.releases.chipSpotify, t.releases.chipApple];

  return (
    <section id="music" data-testid="releases-section" className={SECTION_CLS}>
      <SectionHeading
        index={t.releases.index}
        eyebrow={t.releases.eyebrow}
        title={t.releases.title}
        testId="releases-heading"
      />
      {items.length === 0 ? (
        <Reveal>
          <div
            data-testid="releases-pending"
            className="border border-if-line bg-if-surface/40 p-10 md:p-16"
          >
            <p className="max-w-xl text-base font-light leading-relaxed text-if-dim">
              {t.releases.pending}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              {chips.map((c) => (
                <span
                  key={c}
                  data-testid={`release-link-slot-${c.toLowerCase().replace(/\s/g, "-")}`}
                  className="flex items-center gap-3 border border-if-line px-5 py-2.5 font-sans text-[11px] uppercase tracking-[0.2em] text-if-dim"
                >
                  {c}
                  <span className="text-if-gold/70">· {t.releases.reserved}</span>
                </span>
              ))}
            </div>
            <p className="mt-8 font-sans text-[10px] uppercase tracking-[0.25em] text-if-dim/70">
              {t.releases.linksNote}
            </p>
          </div>
        </Reveal>
      ) : (
        <div className="grid gap-px border border-if-line bg-if-line md:grid-cols-[repeat(auto-fit,minmax(340px,1fr))]">
          {items.map((r, i) => (
            <Reveal key={r.id} delay={i * 0.1} className="h-full">
              <article
                data-testid={`release-card-${r.id}`}
                className="flex h-full flex-col justify-between bg-if-black p-10"
              >
                <div>
                  {(r.format || r.year) && (
                    <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-if-gold">
                      {[r.format, r.year].filter(Boolean).join(" · ")}
                    </p>
                  )}
                  <h3 className="mt-6 font-serif text-3xl font-light text-if-text">
                    {r.title}
                  </h3>
                  <p className="mt-2 text-sm text-if-dim">{r.artist}</p>
                </div>
                <div className="mt-10 flex flex-wrap gap-3">
                  {r.links?.youtube ? (
                    <a
                      href={r.links.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`release-${r.id}-youtube`}
                      className="border border-if-gold/50 px-4 py-2 font-sans text-[10px] uppercase tracking-[0.2em] text-if-gold transition-colors duration-300 hover:bg-if-gold hover:text-if-black"
                    >
                      {t.releases.watchYoutube}
                    </a>
                  ) : (
                    <span
                      data-testid={`release-${r.id}-slot-youtube`}
                      className="border border-if-line px-4 py-2 font-sans text-[10px] uppercase tracking-[0.2em] text-if-dim"
                    >
                      {t.releases.chipYT} · {t.releases.reserved}
                    </span>
                  )}
                  {r.links?.spotify && (
                    <a
                      href={r.links.spotify}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`release-${r.id}-spotify`}
                      className="border border-if-gold/50 px-4 py-2 font-sans text-[10px] uppercase tracking-[0.2em] text-if-gold transition-colors duration-300 hover:bg-if-gold hover:text-if-black"
                    >
                      {t.releases.chipSpotify}
                    </a>
                  )}
                  {r.links?.appleMusic && (
                    <a
                      href={r.links.appleMusic}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`release-${r.id}-apple`}
                      className="border border-if-gold/50 px-4 py-2 font-sans text-[10px] uppercase tracking-[0.2em] text-if-gold transition-colors duration-300 hover:bg-if-gold hover:text-if-black"
                    >
                      {t.releases.chipApple}
                    </a>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      )}
    </section>
  );
};
